import { EventEmitter } from "eventemitter3";
import { Howl, Howler } from "howler/dist/howler.core.min.js";

import { beepSound, laserActiveImage, laserPausedImage, scanditLogoImage } from "./assets/base64assets";

import { userLicenseKey } from "../index";
import { BarcodePickerCameraManager } from "./barcodePickerCameraManager";
import { BrowserHelper } from "./browserHelper";
import { Camera } from "./camera";
import { CameraSettings } from "./cameraSettings";
import { CustomError } from "./customError";
import { ImageSettings } from "./imageSettings";
import { Parser } from "./parser";
import { Scanner } from "./scanner";
import { ScanResult } from "./scanResult";
import { ScanSettings } from "./scanSettings";

/**
 * A barcode picker element used to get and show camera input and perform scanning operations.
 *
 * The barcode picker will automatically fit and scale inside the given <i>originElement</i>.
 *
 * Each barcode picker internally contains a [[Scanner]] object with its own WebWorker thread running a
 * separate copy of the external Scandit Engine library. To optimize loading times and performance it's
 * recommended to reuse the same picker and to already create the picker in advance (hidden) and just
 * display it when needed whenever possible.
 *
 * As the loading of the external Scandit Engine library can take some time the picker always starts inactive
 * (but showing GUI and video) and then activates, if not paused, as soon as the library is ready to scan.
 * The [[onReady]] method can be used to set up a listener function to be called when the library is loaded.
 *
 * You are not allowed to hide the Scandit logo present in the corner of the GUI.
 */
export class BarcodePicker {
  /**
   * @hidden
   */
  public static readonly grandParentElementClassName: string = "scandit scandit-container";
  /**
   * @hidden
   */
  public static readonly parentElementClassName: string = "scandit scandit-barcode-picker";
  /**
   * @hidden
   */
  public static readonly videoElementClassName: string = "scandit-video";
  /**
   * @hidden
   */
  public static readonly scanditLogoImageElementClassName: string = "scandit-logo";
  /**
   * @hidden
   */
  public static readonly laserImageElementClassName: string = "scandit-laser";
  /**
   * @hidden
   */
  public static readonly viewfinderElementClassName: string = "scandit-viewfinder";
  /**
   * @hidden
   */
  public static readonly cameraSwitcherElementClassName: string = "scandit-camera-switcher";
  /**
   * @hidden
   */
  public static readonly torchToggleElementClassName: string = "scandit-torch-toggle";

  /**
   * @hidden
   */
  public readonly grandParentElement: HTMLDivElement;
  /**
   * @hidden
   */
  public readonly parentElement: HTMLDivElement;
  /**
   * @hidden
   */
  public readonly videoElement: HTMLVideoElement;
  /**
   * @hidden
   */
  public readonly laserActiveImageElement: HTMLImageElement;
  /**
   * @hidden
   */
  public readonly laserPausedImageElement: HTMLImageElement;
  /**
   * @hidden
   */
  public readonly viewfinderElement: HTMLDivElement;

  private cameraManager: BarcodePickerCameraManager;
  private visible: boolean;
  private playSoundOnScan: boolean;
  private vibrateOnScan: boolean;
  private scanningPaused: boolean;
  private guiStyle: BarcodePicker.GuiStyle;
  private eventEmitter: EventEmitter;
  private scanner: Scanner;
  private mirrorImage: boolean;
  private videoImageCanvasContext: CanvasRenderingContext2D;
  private fatalError: Error;
  private beepSound: Howl;
  private vibrateFunction: (pattern: number | number[]) => boolean;
  private latestVideoTimeProcessed: number;
  private resizeInterval: number;
  private lastKnownOriginElementWidth: number;
  private lastKnownOriginElementHeight: number;
  private destroyed: boolean;
  private isReadyToWork: boolean;
  private cameraAccess: boolean;
  private globalListener: EventListenerOrEventListenerObject;

  private constructor(
    private originElement: HTMLElement,
    {
      visible,
      playSoundOnScan,
      vibrateOnScan,
      scanningPaused,
      guiStyle,
      scanSettings
    }: {
      visible?: boolean;
      playSoundOnScan?: boolean;
      vibrateOnScan?: boolean;
      scanningPaused?: boolean;
      guiStyle?: BarcodePicker.GuiStyle;
      scanSettings?: ScanSettings;
    } = {}
  ) {
    this.isReadyToWork = false;
    this.destroyed = false;
    this.scanningPaused = scanningPaused;

    Howler.autoSuspend = false;
    this.beepSound = new Howl({
      src: beepSound
    });

    this.vibrateFunction =
      <any>navigator.vibrate ||
      (<any>navigator).webkitVibrate ||
      (<any>navigator).mozVibrate ||
      (<any>navigator).msVibrate;

    this.eventEmitter = new EventEmitter();

    this.grandParentElement = document.createElement("div");
    this.grandParentElement.className = BarcodePicker.grandParentElementClassName;
    this.originElement.appendChild(this.grandParentElement);
    this.parentElement = document.createElement("div");
    this.parentElement.className = BarcodePicker.parentElementClassName;
    this.grandParentElement.appendChild(this.parentElement);

    this.videoElement = document.createElement("video");
    this.setupVideoElement(this.videoElement);

    this.setupVideoImageCanvasElement(document.createElement("canvas"));

    this.setVisible(visible);
    this.setPlaySoundOnScanEnabled(playSoundOnScan);
    this.setVibrateOnScanEnabled(vibrateOnScan);

    this.scanner = new Scanner({ scanSettings: scanSettings });
    this.scanner.onReady(() => {
      this.isReadyToWork = true;
      this.eventEmitter.emit("ready");
    });

    window.requestAnimationFrame(this.videoProcessing.bind(this));

    this.resizeInterval = window.setInterval(() => {
      this.resizeIfNeeded();
    }, 250);

    this.laserActiveImageElement = document.createElement("img");
    this.laserPausedImageElement = document.createElement("img");
    this.viewfinderElement = document.createElement("div");
    this.setupAssets();
    this.setGuiStyle(guiStyle);

    this.setupGlobalListeners();

    this.cameraManager = new BarcodePickerCameraManager(this);
  }

  /**
   * Creates a BarcodePicker instance, creating the needed HTML in the given origin element.
   * If the <i>accessCamera</i> option is enabled (active by default) the available cameras are accessed
   * and a camera access permission is requested to the user if needed.
   * This object expects that at least a camera is available. The active camera is accessed and kept active during the
   * lifetime of the picker (also when hidden or scanning is paused), and is only released when [[destroy]] is called.
   *
   * It is required to having configured the library via [[configure]] before this object can be created.
   *
   * By default an alert is shown if an internal error during scanning is encountered which prevents the scanning
   * procedure from continuing. As this uses the built-in [[onScanError]] event functionality, if unwanted it can be
   * disabled by calling [[removeScanErrorListeners]] on the BarcodePicker instance (right after creation).
   *
   * Depending on parameters, device features and user permissions for camera access, any of the following errors
   * could be the rejected result of the returned promise:
   * - <tt>LibraryNotConfiguredError</tt>
   * - <tt>NoOriginElementError</tt>
   * - <tt>UnsupportedBrowserError</tt>
   * - <tt>PermissionDeniedError</tt>
   * - <tt>NotAllowedError</tt>
   * - <tt>NotFoundError</tt>
   * - <tt>AbortError</tt>
   * - <tt>NotReadableError</tt>
   * - <tt>InternalError</tt>
   * - <tt>NoCameraAvailableError</tt>
   *
   * @param originElement The HTMLElement inside which all the necessary elements for the picker will be added.
   * @param visible <div class="tsd-signature-symbol">Default =&nbsp;true</div>
   * Whether the picker starts in a visible state.
   * @param playSoundOnScan <div class="tsd-signature-symbol">Default =&nbsp;false</div>
   * Whether a sound is played on barcode scanned (iOS requires user input).
   * @param vibrateOnScan <div class="tsd-signature-symbol">Default =&nbsp;false</div>
   * Whether the device vibrates on barcode scanned (only Chrome & Firefox, requires user input).
   * @param scanningPaused <div class="tsd-signature-symbol">Default =&nbsp;false</div>
   * Whether the picker starts in a paused scanning state.
   * @param guiStyle <div class="tsd-signature-symbol">Default =&nbsp;GuiStyle.LASER</div>
   * The GUI style for the picker.
   * @param enableCameraSwitcher <div class="tsd-signature-symbol">Default =&nbsp;true</div>
   * Whether to show a GUI button to switch between different cameras (when available).
   * @param enableTorchToggle <div class="tsd-signature-symbol">Default =&nbsp;true</div>
   * Whether to show a GUI button to toggle device torch on/off (when available, only Chrome).
   * @param enableTapToFocus <div class="tsd-signature-symbol">Default =&nbsp;true</div>
   * Whether to trigger a manual focus of the camera when clicking/tapping on the video (when available, only Chrome).
   * @param enablePinchToZoom <div class="tsd-signature-symbol">Default =&nbsp;true</div>
   * Whether to control the zoom of the camera when doing a pinching gesture on the video (when available, only Chrome).
   * @param accessCamera <div class="tsd-signature-symbol">Default =&nbsp;true</div>
   * Whether to immediately access the camera (and requesting user permissions if needed) on picker creation.
   * @param camera <div class="tsd-signature-symbol">Default =&nbsp;undefined</div>
   * The camera to be used for video input, if not specified the back or only camera will be used.
   * @param cameraSettings <div class="tsd-signature-symbol">Default =&nbsp;undefined</div>
   * The camera options used when accessing the camera, by default HD resolution is used.
   * @param scanSettings <div class="tsd-signature-symbol">Default =&nbsp;new ScanSettings()</div>
   * The configuration object for scanning options (All symbologies disabled by default).
   * @returns A promise resolving to the created ready [[BarcodePicker]] object.
   */
  public static create(
    originElement: HTMLElement,
    {
      visible = true,
      playSoundOnScan = false,
      vibrateOnScan = false,
      scanningPaused = false,
      guiStyle = BarcodePicker.GuiStyle.LASER,
      scanSettings = new ScanSettings(),
      enableCameraSwitcher = true,
      enableTorchToggle = true,
      enableTapToFocus = true,
      enablePinchToZoom = true,
      accessCamera = true,
      camera,
      cameraSettings
    }: {
      visible?: boolean;
      playSoundOnScan?: boolean;
      vibrateOnScan?: boolean;
      scanningPaused?: boolean;
      uiStyle?: BarcodePicker.GuiStyle;
      guiStyle?: BarcodePicker.GuiStyle;
      scanSettings?: ScanSettings;
      enableCameraSwitcher?: boolean;
      enableTorchToggle?: boolean;
      enableTapToFocus?: boolean;
      enablePinchToZoom?: boolean;
      accessCamera?: boolean;
      camera?: Camera;
      cameraSettings?: CameraSettings;
    } = {}
  ): Promise<BarcodePicker> {
    const unsupportedBrowserError: Error | undefined = BrowserHelper.checkBrowserCompatibility();
    if (unsupportedBrowserError != null) {
      return Promise.reject(unsupportedBrowserError);
    }
    if (userLicenseKey == null || userLicenseKey.trim() === "") {
      return Promise.reject(
        new CustomError({
          name: "LibraryNotConfiguredError",
          message: "The library has not correctly been configured yet, please call 'configure' with valid parameters"
        })
      );
    }
    if (!(originElement instanceof HTMLElement)) {
      return Promise.reject(
        new CustomError({
          name: "NoOriginElementError",
          message: "A valid origin HTML element must be given"
        })
      );
    }

    const barcodePicker: BarcodePicker = new BarcodePicker(originElement, {
      visible,
      playSoundOnScan,
      vibrateOnScan,
      scanningPaused,
      guiStyle,
      scanSettings
    });

    barcodePicker.cameraManager.setUIOptions(
      enableCameraSwitcher,
      enableTorchToggle,
      enableTapToFocus,
      enablePinchToZoom
    );
    barcodePicker.cameraManager.setSelectedCamera(camera);
    barcodePicker.cameraManager.setSelectedCameraSettings(cameraSettings);

    barcodePicker.cameraAccess = accessCamera;

    // Show error in alert on ScanError by default for easier customer debugging
    barcodePicker.onScanError(error => {
      alert(error);
    });

    if (accessCamera) {
      return barcodePicker.cameraManager.setupCameras();
    }

    return Promise.resolve(barcodePicker);
  }

  /**
   * Stop scanning and displaying video output, remove HTML elements added to the page,
   * destroy the internal [[Scanner]] and destroy the barcode picker itself; ensuring complete cleanup.
   *
   * This method should be called after you don't plan to use the picker anymore,
   * before the object is automatically cleaned up by JavaScript.
   * The barcode picker must not be used in any way after this call.
   */
  public destroy(): void {
    this.pauseScanning(true);
    this.destroyed = true;
    this.scanner.destroy();
    this.eventEmitter.removeAllListeners();
    window.clearInterval(this.resizeInterval);
    this.removeGlobalListeners();
    this.grandParentElement.remove();
    this.originElement.classList.remove("scandit-hidden");
  }

  /**
   * Applies a new set of scan settings to the internal scanner (replacing old settings).
   *
   * @param scanSettings The scan configuration object to be applied to the scanner.
   * @returns The updated [[BarcodePicker]] object.
   */
  public applyScanSettings(scanSettings: ScanSettings): BarcodePicker {
    this.scanner.applyScanSettings(scanSettings);

    return this;
  }

  /**
   * @returns Whether the scanning is currently paused.
   */
  public isScanningPaused(): boolean {
    return this.scanningPaused;
  }

  /**
   * Pause the recognition of codes in the input image.
   *
   * By default video from the camera is still shown, if the <i>pauseCamera</i> option is enabled the camera stream
   * is paused (camera access is fully interrupted) and will be resumed when calling [[resumeScanning]],
   * possibly requesting user permissions if needed.
   *
   * @param pauseCamera Whether to also pause the camera stream.
   * @returns The updated [[BarcodePicker]] object.
   */
  public pauseScanning(pauseCamera: boolean = false): BarcodePicker {
    this.scanningPaused = true;

    if (pauseCamera) {
      this.cameraManager.stopStream();
    }

    if (this.scanner.isReady()) {
      this.laserActiveImageElement.classList.add("scandit-hidden-opacity");
      this.laserPausedImageElement.classList.remove("scandit-hidden-opacity");
      this.viewfinderElement.classList.add("paused");
    }

    return this;
  }

  /**
   * Resume the recognition of codes in the input image.
   *
   * If the camera stream was stopped when calling [[pauseScanning]], the camera stream is also resumed and
   * user permissions are requested if needed to resume video input.
   *
   * @returns The updated [[BarcodePicker]] object.
   */
  public resumeScanning(): Promise<BarcodePicker> {
    this.scanningPaused = false;

    if (this.scanner.isReady()) {
      this.laserPausedImageElement.classList.add("scandit-hidden-opacity");
      this.laserActiveImageElement.classList.remove("scandit-hidden-opacity");
      this.viewfinderElement.classList.remove("paused");
    }

    if (this.cameraManager.activeCamera == null && this.cameraAccess) {
      return this.cameraManager.setupCameras();
    }

    return Promise.resolve(this);
  }

  /**
   * @returns The currently active camera.
   */
  public getActiveCamera(): Camera | undefined {
    return this.cameraManager.activeCamera;
  }

  /**
   * Select a camera to be used for video input.
   *
   * If camera access is enabled, the camera is enabled and accessed.
   *
   * Depending on device features and user permissions for camera access, any of the following errors
   * could be the rejected result of the returned promise:
   * - <tt>PermissionDeniedError</tt>
   * - <tt>NotAllowedError</tt>
   * - <tt>NotFoundError</tt>
   * - <tt>AbortError</tt>
   * - <tt>NotReadableError</tt>
   * - <tt>InternalError</tt>
   * - <tt>NoCameraAvailableError</tt>
   *
   * @param camera The new camera to be used.
   * @param cameraSettings The camera options used when accessing the camera, by default HD resolution is used.
   * @returns A promise resolving to the updated [[BarcodePicker]] object when the camera is set
   * (and accessed, if camera access is currently enabled).
   */
  public setActiveCamera(camera: Camera, cameraSettings?: CameraSettings): Promise<BarcodePicker> {
    if (!this.cameraAccess) {
      this.cameraManager.setSelectedCamera(camera);
      this.cameraManager.setSelectedCameraSettings(cameraSettings);

      return Promise.resolve(this);
    }

    return this.cameraManager.initializeCameraWithSettings(camera, cameraSettings);
  }

  /**
   * Try to apply new settings to the currently used camera for video input,
   * if no settings are passed the default ones are set.
   *
   * If camera access is enabled, the camera is updated and accessed with the new settings.
   *
   * Depending on device features and user permissions for camera access, any of the following errors
   * could be the rejected result of the returned promise:
   * - <tt>PermissionDeniedError</tt>
   * - <tt>NotAllowedError</tt>
   * - <tt>NotFoundError</tt>
   * - <tt>AbortError</tt>
   * - <tt>NotReadableError</tt>
   * - <tt>InternalError</tt>
   * - <tt>NoCameraAvailableError</tt>
   *
   * @param cameraSettings The new camera options used when accessing the camera, by default HD resolution is used.
   * @returns A promise resolving to the updated [[BarcodePicker]] object when the camera is updated
   * (and accessed, if camera access is currently enabled).
   */
  public applyCameraSettings(cameraSettings?: CameraSettings): Promise<BarcodePicker> {
    if (!this.cameraAccess) {
      this.cameraManager.setSelectedCameraSettings(cameraSettings);

      return Promise.resolve(this);
    }

    return this.cameraManager.applyCameraSettings(cameraSettings);
  }

  /**
   * @returns Whether the picker is in a visible state or not.
   */
  public isVisible(): boolean {
    return this.visible;
  }

  /**
   * Enable or disable picker visibility.
   *
   * @param visible Whether the picker is in a visible state or not.
   * @returns The updated [[BarcodePicker]] object.
   */
  public setVisible(visible: boolean): BarcodePicker {
    this.visible = visible;
    if (visible) {
      this.originElement.classList.remove("scandit-hidden");
    } else {
      this.originElement.classList.add("scandit-hidden");
    }

    return this;
  }

  /**
   * @returns Whether the camera video is mirrored along the vertical axis.
   */
  public isMirrorImageEnabled(): boolean {
    return this.mirrorImage;
  }

  /**
   * Enable or disable camera video mirroring along the vertical axis.
   *
   * @param enabled Whether the camera video is mirrored along the vertical axis.
   * @returns The updated [[BarcodePicker]] object.
   */
  public setMirrorImageEnabled(enabled: boolean): BarcodePicker {
    this.mirrorImage = enabled;
    if (enabled) {
      this.videoElement.classList.add("mirrored");
    } else {
      this.videoElement.classList.remove("mirrored");
    }

    return this;
  }

  /**
   * @returns Whether a sound should be played on barcode recognition (iOS requires user input).
   */
  public isPlaySoundOnScanEnabled(): boolean {
    return this.playSoundOnScan;
  }

  /**
   * Enable or disable playing a sound on barcode recognition (iOS requires user input).
   *
   * @param enabled Whether a sound should be played on barcode recognition.
   * @returns The updated [[BarcodePicker]] object.
   */
  public setPlaySoundOnScanEnabled(enabled: boolean): BarcodePicker {
    this.playSoundOnScan = enabled;

    return this;
  }

  /**
   * @returns Whether the device should vibrate on barcode recognition (only Chrome & Firefox, requires user input).
   */
  public isVibrateOnScanEnabled(): boolean {
    return this.vibrateOnScan;
  }

  /**
   * Enable or disable vibrating the device on barcode recognition (only Chrome & Firefox, requires user input).
   *
   * @param enabled Whether the device should vibrate on barcode recognition.
   * @returns The updated [[BarcodePicker]] object.
   */
  public setVibrateOnScanEnabled(enabled: boolean): BarcodePicker {
    this.vibrateOnScan = enabled;

    return this;
  }

  /**
   * Enable or disable the torch/flashlight of the device (when available, only Chrome).
   * Changing active camera or camera settings will cause the torch to become disabled.
   *
   * A button on the [[BarcodePicker]] GUI to let the user toggle this functionality can also be set
   * on creation via the <i>enableTorchToggle</i> option (enabled by default, when available).
   *
   * @param enabled Whether the torch should be enabled or disabled.
   * @returns The updated [[BarcodePicker]] object.
   */
  public setTorchEnabled(enabled: boolean): BarcodePicker {
    this.cameraManager.setTorchEnabled(enabled);

    return this;
  }

  /**
   * Set the zoom level of the device (when available, only Chrome).
   * Changing active camera or camera settings will cause the zoom to be reset.
   *
   * @param zoomPercentage The percentage of the max zoom (between 0 and 1).
   * @returns The updated [[BarcodePicker]] object.
   */
  public setZoom(zoomPercentage: number): BarcodePicker {
    this.cameraManager.setZoom(zoomPercentage);

    return this;
  }

  /**
   * @returns Whether the barcode picker has loaded the external Scandit Engine library and is ready to scan.
   */
  public isReady(): boolean {
    return this.isReadyToWork;
  }

  /**
   * Add the listener function to the listeners array for the "ready" event, fired when the external
   * Scandit Engine library has been loaded and the barcode picker can thus start to scan barcodes.
   *
   * No checks are made to see if the listener has already been added.
   * Multiple calls passing the same listener will result in the listener being added, and called, multiple times.
   *
   * @param listener The listener function.
   * @returns The updated [[BarcodePicker]] object.
   */
  public onReady(listener: () => void): BarcodePicker {
    if (this.isReadyToWork) {
      listener();
    } else {
      this.eventEmitter.once("ready", listener, this);
    }

    return this;
  }

  /**
   * Add the listener function to the listeners array for the "scan" event, fired when new barcodes
   * are recognized in the image frame. The returned barcodes are affected
   * by the [[ScanSettings.setCodeDuplicateFilter]] option.
   *
   * No checks are made to see if the listener has already been added.
   * Multiple calls passing the same listener will result in the listener being added, and called, multiple times.
   *
   * @param listener The listener function, which will be invoked with a [[ScanResult]] object.
   * @param once Whether the listener should just be triggered only once and then discarded.
   * @returns The updated [[BarcodePicker]] object.
   */
  public onScan(listener: (scanResult: ScanResult) => void, once?: boolean): BarcodePicker {
    if (once === true) {
      this.eventEmitter.once("scan", listener, this);
    } else {
      this.eventEmitter.on("scan", listener, this);
    }

    return this;
  }

  /**
   * Removes the specified listener from the "scan" event listener array.
   * This will remove, at most, one instance of a listener from the listener array.
   * If any single listener has been added multiple times then this method must
   * be called multiple times to remove each instance.
   *
   * @param listener The listener function to be removed.
   * @returns The updated [[BarcodePicker]] object.
   */
  public removeScanListener(listener: (scanResult: ScanResult) => void): BarcodePicker {
    this.eventEmitter.removeListener("scan", listener);

    return this;
  }

  /**
   * Removes all listeners from the "scan" event listener array.
   *
   * @returns The updated [[BarcodePicker]] object.
   */
  public removeScanListeners(): BarcodePicker {
    this.eventEmitter.removeAllListeners("scan");

    return this;
  }

  /**
   * Add the listener function to the listeners array for the "scan error" event, fired when an error occurs
   * during scanning initialization and execution. The barcode picker will be automatically paused when this happens.
   *
   * No checks are made to see if the listener has already been added.
   * Multiple calls passing the same listener will result in the listener being added, and called, multiple times.
   *
   * @param listener The listener function, which will be invoked with an <tt>ScanditEngineError</tt> object.
   * @param once Whether the listener should just be triggered only once and then discarded.
   * @returns The updated [[BarcodePicker]] object.
   */
  public onScanError(listener: (error: Error) => void, once?: boolean): BarcodePicker {
    if (once === true) {
      this.eventEmitter.once("scan-error", listener, this);
    } else {
      this.eventEmitter.on("scan-error", listener, this);
    }

    return this;
  }

  /**
   * Removes the specified listener from the "scan error" event listener array.
   * This will remove, at most, one instance of a listener from the listener array.
   * If any single listener has been added multiple times then this method must
   * be called multiple times to remove each instance.
   *
   * @param listener The listener function to be removed.
   * @returns The updated [[BarcodePicker]] object.
   */
  public removeScanErrorListener(listener: (scanResult: ScanResult) => void): BarcodePicker {
    this.eventEmitter.removeListener("scan-error", listener);

    return this;
  }

  /**
   * Removes all listeners from the "scan error" event listener array.
   *
   * @returns The updated [[BarcodePicker]] object.
   */
  public removeScanErrorListeners(): BarcodePicker {
    this.eventEmitter.removeAllListeners("scan-error");

    return this;
  }

  /**
   * Set the GUI style for the picker.
   *
   * @param guiStyle The new GUI style to be applied.
   * @returns The updated [[BarcodePicker]] object.
   */
  public setGuiStyle(guiStyle: BarcodePicker.GuiStyle): BarcodePicker {
    this.guiStyle = guiStyle;

    switch (this.guiStyle) {
      case BarcodePicker.GuiStyle.NONE:
        this.laserActiveImageElement.classList.add("scandit-hidden");
        this.laserPausedImageElement.classList.add("scandit-hidden");
        this.viewfinderElement.classList.add("scandit-hidden");
        break;
      case BarcodePicker.GuiStyle.LASER:
        this.laserActiveImageElement.classList.remove("scandit-hidden");
        this.laserPausedImageElement.classList.remove("scandit-hidden");
        this.viewfinderElement.classList.add("scandit-hidden");
        break;
      case BarcodePicker.GuiStyle.VIEWFINDER:
        this.laserActiveImageElement.classList.add("scandit-hidden");
        this.laserPausedImageElement.classList.add("scandit-hidden");
        this.viewfinderElement.classList.remove("scandit-hidden");
        break;
      default:
        break;
    }

    return this;
  }

  /**
   * Access the currently set or default camera, requesting user permissions if needed.
   * This method is meant to be used after the picker has been initialized with disabled camera access
   * (<i>cameraAccess</i>=false). Calling this doesn't do anything if the camera has already been accessed.
   *
   * Depending on device features and user permissions for camera access, any of the following errors
   * could be the rejected result of the returned promise:
   * - <tt>PermissionDeniedError</tt>
   * - <tt>NotAllowedError</tt>
   * - <tt>NotFoundError</tt>
   * - <tt>AbortError</tt>
   * - <tt>NotReadableError</tt>
   * - <tt>InternalError</tt>
   * - <tt>NoCameraAvailableError</tt>
   *
   * @returns A promise resolving to the updated [[BarcodePicker]] object when the camera is accessed.
   */
  public accessCamera(): Promise<BarcodePicker> {
    if (!this.cameraAccess) {
      this.cameraAccess = true;

      return this.cameraManager.setupCameras();
    }

    return Promise.resolve(this);
  }

  /**
   * Create a new parser object.
   *
   * @param dataFormat The format of the input data for the parser.
   * @returns The newly created parser.
   */
  public createParserForFormat(dataFormat: Parser.DataFormat): Parser {
    return this.scanner.createParserForFormat(dataFormat);
  }

  /**
   * Reassign the barcode picker to a different HTML element.
   *
   * All the barcode picker elements inside the current origin element will be moved to the new given one.
   *
   * @param originElement The HTMLElement into which all the necessary elements for the picker will be moved.
   * @returns The updated [[BarcodePicker]] object.
   */
  public reassignOriginElement(originElement: HTMLElement): BarcodePicker {
    if (!(originElement instanceof HTMLElement)) {
      throw new CustomError({
        name: "NoOriginElementError",
        message: "A valid origin HTML element must be given"
      });
    }

    if (!this.visible) {
      this.originElement.classList.remove("scandit-hidden");
      originElement.classList.add("scandit-hidden");
    }

    originElement.appendChild(this.grandParentElement);
    this.checkAndRecoverPlayback();
    this.resizeIfNeeded();

    this.originElement = originElement;

    return this;
  }

  private resizeIfNeeded(): void {
    if (
      this.videoElement.videoHeight < 1 ||
      this.originElement.clientHeight < 1 ||
      (this.lastKnownOriginElementWidth === this.originElement.clientWidth &&
        this.lastKnownOriginElementHeight === this.originElement.clientHeight)
    ) {
      return;
    }

    this.parentElement.style.maxWidth = null;
    this.parentElement.style.maxHeight = null;

    const videoRatio: number = this.videoElement.videoWidth / this.videoElement.videoHeight;
    let width: number = this.originElement.clientWidth;
    let height: number = this.originElement.clientHeight;

    if (videoRatio > this.originElement.clientWidth / this.originElement.clientHeight) {
      height = width / videoRatio;
    } else {
      width = height * videoRatio;
    }

    this.lastKnownOriginElementWidth = this.originElement.clientWidth;
    this.lastKnownOriginElementHeight = this.originElement.clientHeight;

    this.parentElement.style.maxWidth = `${Math.ceil(width)}px`;
    this.parentElement.style.maxHeight = `${Math.ceil(height)}px`;
  }

  private setupVideoElement(videoElement: HTMLVideoElement): void {
    videoElement.setAttribute("autoplay", "autoplay");
    videoElement.setAttribute("playsinline", "true");
    videoElement.setAttribute("muted", "muted");
    videoElement.className = BarcodePicker.videoElementClassName;

    videoElement.addEventListener("canplay", () => {
      if (this.cameraManager.activeCamera != null) {
        // Retrigger resize
        this.lastKnownOriginElementWidth = 0;
        this.lastKnownOriginElementHeight = 0;

        this.cameraManager.activeCamera.currentResolution = {
          width: videoElement.videoWidth,
          height: videoElement.videoHeight
        };
        this.videoImageCanvasContext.canvas.width = this.cameraManager.activeCamera.currentResolution.width;
        this.videoImageCanvasContext.canvas.height = this.cameraManager.activeCamera.currentResolution.height;

        this.scanner.applyImageSettings({
          width: videoElement.videoWidth,
          height: videoElement.videoHeight,
          format: ImageSettings.Format.RGBA_8U
        });
      }
    });

    this.parentElement.appendChild(videoElement);
  }

  private setupVideoImageCanvasElement(videoImageCanvasElement: HTMLCanvasElement): void {
    const canvasContext: CanvasRenderingContext2D | null = videoImageCanvasElement.getContext("2d");
    if (canvasContext != null) {
      this.videoImageCanvasContext = canvasContext;
    }
  }

  private flashLaser(): void {
    this.laserActiveImageElement.classList.remove("scandit-flash-color");
    // tslint:disable-next-line:no-unused-expression
    this.laserActiveImageElement.offsetHeight; // Trigger reflow to restart animation
    this.laserActiveImageElement.classList.add("scandit-flash-color");
  }

  private flashViewfinder(): void {
    this.viewfinderElement.classList.remove("scandit-flash-white");
    // tslint:disable-next-line:no-unused-expression
    this.viewfinderElement.offsetHeight; // Trigger reflow to restart animation
    this.viewfinderElement.classList.add("scandit-flash-white");
  }

  private setupAssets(): void {
    const scanditLogoImageElement: HTMLImageElement = document.createElement("img");
    scanditLogoImageElement.src = scanditLogoImage;
    scanditLogoImageElement.className = BarcodePicker.scanditLogoImageElementClassName;
    this.parentElement.appendChild(scanditLogoImageElement);

    this.laserActiveImageElement.src = laserActiveImage;
    this.laserActiveImageElement.className = BarcodePicker.laserImageElementClassName;
    this.parentElement.appendChild(this.laserActiveImageElement);

    this.laserPausedImageElement.src = laserPausedImage;
    this.laserPausedImageElement.className = BarcodePicker.laserImageElementClassName;
    this.parentElement.appendChild(this.laserPausedImageElement);

    this.viewfinderElement.className = BarcodePicker.viewfinderElementClassName;
    this.parentElement.appendChild(this.viewfinderElement);

    // Show inactive GUI, as for now the scanner isn't ready yet
    this.laserActiveImageElement.classList.add("scandit-hidden-opacity");
    this.laserPausedImageElement.classList.remove("scandit-hidden-opacity");
    this.viewfinderElement.classList.add("paused");
  }

  private checkAndRecoverPlayback(): void {
    if (this.cameraManager.activeCamera != null && this.videoElement != null && this.videoElement.srcObject != null) {
      if (!this.videoElement.srcObject.active) {
        this.cameraManager.reinitializeCamera().catch(error => {
          this.fatalError = error;
          console.error(error);
        });
      } else {
        const playPromise: Promise<void> = this.videoElement.play();

        if (playPromise != null) {
          playPromise.catch(() => {
            // Can sometimes cause an incorrect rejection (all is good, ignore).
          });
        }
      }
    }
  }

  private setupGlobalListeners(): void {
    this.globalListener = this.checkAndRecoverPlayback.bind(this);
    document.addEventListener("visibilitychange", this.globalListener);
  }

  private removeGlobalListeners(): void {
    document.removeEventListener("visibilitychange", this.globalListener);
  }

  private videoProcessing(): void {
    if (this.destroyed) {
      return;
    }

    if (
      this.cameraManager.activeCamera == null ||
      this.cameraManager.activeCamera.currentResolution == null ||
      this.fatalError != null ||
      this.scanningPaused ||
      !this.scanner.isReady() ||
      this.scanner.isBusyProcessing() ||
      this.latestVideoTimeProcessed === this.videoElement.currentTime
    ) {
      window.requestAnimationFrame(this.videoProcessing.bind(this));

      return;
    }

    if (this.latestVideoTimeProcessed == null) {
      // Show active GUI if needed, as now it's the moment the scanner is ready and used for the first time
      this.resumeScanning();
    }

    this.latestVideoTimeProcessed = this.videoElement.currentTime;

    this.videoImageCanvasContext.drawImage(this.videoElement, 0, 0);
    try {
      const imageData: Uint8ClampedArray = this.videoImageCanvasContext.getImageData(
        0,
        0,
        this.videoImageCanvasContext.canvas.width,
        this.videoImageCanvasContext.canvas.height
      ).data;
      this.scanner
        .processImage(imageData)
        .then(scanResult => {
          if (!this.scanningPaused) {
            if (scanResult.barcodes.length !== 0) {
              if (this.guiStyle === BarcodePicker.GuiStyle.LASER) {
                this.flashLaser();
              } else if (this.guiStyle === BarcodePicker.GuiStyle.VIEWFINDER) {
                this.flashViewfinder();
              }
              if (this.playSoundOnScan) {
                this.beepSound.play();
              }
              if (this.vibrateOnScan && this.vibrateFunction != null) {
                this.vibrateFunction.call(navigator, 300);
              }

              this.eventEmitter.emit("scan", scanResult);
            }
          }
        })
        .catch(scanError => {
          if (!this.scanningPaused) {
            this.pauseScanning();
            this.eventEmitter.emit("scan-error", scanError);
          }
        });
    } catch (error) {
      this.fatalError = error;
      console.error(error);
    }

    window.requestAnimationFrame(this.videoProcessing.bind(this));
  }
}

// istanbul ignore next
export namespace BarcodePicker {
  /**
   * GUI style to be used by a barcode picker, used to hint barcode placement in the frame.
   */
  export enum GuiStyle {
    /**
     * No GUI is shown to indicate where the barcode should be placed.
     * Be aware that the Scandit logo continues to be displayed as showing it is part of the license agreement.
     */
    NONE = 0,
    /**
     * A laser line is shown.
     */
    LASER = 1,
    /**
     * A rectangular viewfinder with rounded corners is shown.
     */
    VIEWFINDER = 2
  }
}
